import { z } from 'zod/v4';
import { stringToBooleanSchema } from '../common/index';

// ============================================
// Course Schemas
// ============================================

import { courseTypeEnum, moduleItemTypeEnum } from '../enums';

/**
 * Локализованная строка — строка или объект { lang: value }
 * Принимает как "text", так и {"ru": "текст", "en": "text"}
 */
const localizedStringSchema = z.union([z.string(), z.record(z.string(), z.string())]);

/**
 * Схема пункта расписания дня (time-слот)
 */
export const scheduleDayItemSchema = z.object({
  time: z.string(),
  title: localizedStringSchema,
});

/**
 * Часы проведения курса в формате HH:MM (например "18:00").
 * Для всех типов: ONLINE, MEETING, OFFLINE, WEBINAR.
 */
export const courseHourSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Время должно быть в формате HH:MM');

/**
 * Схема элемента расписания курса (день)
 */
export const courseScheduleItemSchema = z.object({
  date: z.string(), // ISO date string
  title: localizedStringSchema.optional().default(''),
  description: localizedStringSchema.optional(),
  items: z.array(scheduleDayItemSchema).optional().default([]),
});

/**
 * Схема перевода курса
 */
export const courseTranslationSchema = z.object({
  languageCode: z.string().min(2).max(5),
  title: z.string().min(1, 'Название курса обязательно').max(200),
  description: z.string().max(5000).nullable().optional(),
  shortDescription: z.string().max(500).nullable().optional(),
  location: z.string().max(200).nullable().optional(),
  venue: z.string().max(200).nullable().optional(),
});

/**
 * Схема создания курса
 */
export const createCourseSchema = z.object({
  slug: z
    .string()
    .min(3, 'Slug должен быть минимум 3 символа')
    .max(100)
    .regex(/^[a-z0-9-]+$/, 'Slug может содержать только строчные буквы, цифры и дефисы'),
  durationMinutes: z.number().int().positive().nullable().optional(),
  lessonsCount: z.number().int().min(0).default(0),
  thumbnailUrl: z.string().min(1).nullable().optional(),
  thumbnailId: z.number().int().nullable().optional(),
  bannerImageId: z.number().int().positive().nullable().optional(),
  pastImageId: z.number().int().positive().nullable().optional(),
  previewVideoUrl: z.string().min(1).nullable().optional(),
  instructorId: z.number().int().nullable().optional(),
  instructorIds: z.array(z.number().int()).nullable().optional(),
  order: z.number().int().default(0),
  isPublished: z.boolean().default(false),
  // Новые поля для типов курсов
  courseType: courseTypeEnum.default('ONLINE'),
  startDate: z.string().datetime().nullable().optional(), // ISO datetime string
  endDate: z.string().datetime().nullable().optional(), // ISO datetime string
  courseHoursFrom: courseHourSchema.nullable().optional(),
  courseHoursTo: courseHourSchema.nullable().optional(),
  accessDurationDays: z.number().int().positive().nullable().optional(),
  schedule: z.array(courseScheduleItemSchema).optional(),
  scheduleTimezone: z.string().max(50).optional(),
  scheduleNotificationSettings: z.object({
    isEnabled: z.boolean(),
    morningTime: z.string(),
    reminderMinutesBefore: z.number().int(),
    customDaySummaryMessage: localizedStringSchema.optional(),
    customItemReminderMessage: localizedStringSchema.optional(),
  }).nullable().optional(),
  // Встречи
  capacity: z.number().int().positive().nullable().optional(),
  // Ценообразование
  price: z.preprocess(
    (val) => {
      if (val === null || val === undefined) {
        return val;
      }
      if (typeof val === 'number') {
        return val;
      }
      if (typeof val === 'string') {
        const parsed = parseFloat(val);
        return isNaN(parsed) ? val : parsed;
      }
      return val;
    },
    z.number().nonnegative().nullable().optional(),
  ),
  currency: z.string().length(3).default('UZS'),
  isPurchasable: z.boolean().default(false),
  prices: z.record(z.string(), z.unknown()).nullable().optional(),
  baseCurrency: z.string().min(3).max(3).default('UZS'),
  // Drip content
  isDripEnabled: z.boolean().default(false),
  dripSchedule: z.array(z.object({
    moduleId: z.number().int(),
    releaseAfterDays: z.number().int().min(0),
  })).nullable().optional(),
  // Триггеры
  onAccessExpiredTriggerId: z.number().int().nullable().optional(),
  onCourseCompletedTriggerId: z.number().int().nullable().optional(),
  // Языковая аудитория: [] — видно всем, ['ru'] — только ru, ['ru','uz'] — ru или uz
  targetLanguages: z.array(z.string().min(2).max(5)).default([]),
  translations: z.array(courseTranslationSchema).min(1, 'Требуется хотя бы один перевод'),
});

/**
 * Схема обновления курса
 *
 * НЕ используем createCourseSchema.partial() — Zod при partial() сохраняет .default(),
 * и при частичном обновлении поля с дефолтами (courseType, isPublished, currency и др.)
 * перезаписываются дефолтными значениями, даже если клиент их не отправлял.
 */
export const updateCourseSchema = z.object({
  slug: z.string().min(3, 'Slug должен быть минимум 3 символа').max(100).regex(/^[a-z0-9-]+$/, 'Slug может содержать только строчные буквы, цифры и дефисы').optional(),
  durationMinutes: z.number().int().positive().nullable().optional(),
  lessonsCount: z.number().int().min(0).optional(),
  thumbnailUrl: z.string().min(1).nullable().optional(),
  thumbnailId: z.number().int().nullable().optional(),
  bannerImageId: z.number().int().positive().nullable().optional(),
  pastImageId: z.number().int().positive().nullable().optional(),
  previewVideoUrl: z.string().min(1).nullable().optional(),
  instructorId: z.number().int().nullable().optional(),
  instructorIds: z.array(z.number().int()).nullable().optional(),
  order: z.number().int().optional(),
  isPublished: z.boolean().optional(),
  courseType: courseTypeEnum.optional(),
  startDate: z.string().datetime().nullable().optional(),
  endDate: z.string().datetime().nullable().optional(),
  courseHoursFrom: courseHourSchema.nullable().optional(),
  courseHoursTo: courseHourSchema.nullable().optional(),
  accessDurationDays: z.number().int().positive().nullable().optional(),
  schedule: z.array(courseScheduleItemSchema).nullable().optional(),
  scheduleTimezone: z.string().max(50).optional(),
  scheduleNotificationSettings: z.object({
    isEnabled: z.boolean(),
    morningTime: z.string(),
    reminderMinutesBefore: z.number().int(),
    customDaySummaryMessage: localizedStringSchema.optional(),
    customItemReminderMessage: localizedStringSchema.optional(),
  }).nullable().optional(),
  capacity: z.number().int().positive().nullable().optional(),
  price: z.preprocess(
    (val) => {
      if (val === null || val === undefined) { return val; }
      if (typeof val === 'number') { return val; }
      if (typeof val === 'string') {
        const parsed = parseFloat(val);
        return isNaN(parsed) ? val : parsed;
      }
      return val;
    },
    z.number().nonnegative().nullable().optional(),
  ),
  currency: z.string().length(3).optional(),
  isPurchasable: z.boolean().optional(),
  prices: z.record(z.string(), z.unknown()).nullable().optional(),
  baseCurrency: z.string().min(3).max(3).optional(),
  isDripEnabled: z.boolean().optional(),
  dripSchedule: z.array(z.object({
    moduleId: z.number().int(),
    releaseAfterDays: z.number().int().min(0),
  })).nullable().optional(),
  onAccessExpiredTriggerId: z.number().int().nullable().optional(),
  onCourseCompletedTriggerId: z.number().int().nullable().optional(),
  targetLanguages: z.array(z.string().min(2).max(5)).optional(),
  translations: z.array(courseTranslationSchema).optional(),
});

/**
 * Схема фильтров для списка курсов
 */
export const courseFiltersSchema = z.object({
  instructorId: z.coerce.number().int().optional(),
  courseType: courseTypeEnum.optional(),
  isPublished: stringToBooleanSchema,
  showDeleted: stringToBooleanSchema,
  search: z.string().max(200).optional(),
  sortBy: z.enum(['id', 'createdAt', 'updatedAt', 'order', 'price', 'isPublished']).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(500).default(20),
});

/**
 * Схема для изменения порядка курсов
 */
export const reorderCoursesSchema = z.object({
  courseIds: z.array(z.number().int()).min(1, 'Требуется хотя бы один курс'),
});

/**
 * Схема публикации/снятия с публикации
 */
export const publishCourseSchema = z.object({
  isPublished: z.boolean(),
});

// ============================================
// Module Schemas
// ============================================

/**
 * Схема перевода модуля
 */
export const moduleTranslationSchema = z.object({
  languageCode: z.string().min(2).max(5),
  title: z.string().min(1, 'Название модуля обязательно').max(200),
  description: z.string().max(2000).nullable().optional(),
});

/**
 * Схема создания модуля
 */
export const createModuleSchema = z.object({
  translations: z.array(moduleTranslationSchema).min(1, 'Требуется хотя бы один перевод'),
  availableFrom: z.coerce.date().nullable().optional(),
});

/**
 * Схема обновления модуля
 */
export const updateModuleSchema = createModuleSchema.partial();

/**
 * Схема для изменения порядка модулей
 */
export const reorderModulesSchema = z.object({
  moduleIds: z.array(z.number().int()).min(1, 'Требуется хотя бы один модуль'),
});

// ============================================
// Module Item Schemas
// ============================================

/**
 * Схема перевода элемента модуля
 */
export const moduleItemTranslationSchema = z.object({
  languageCode: z.string().min(2).max(5),
  title: z.string().min(1, 'Название урока обязательно').max(200),
  content: z.string().max(50000).nullable().optional(), // Markdown для статей
});

/**
 * Схема создания элемента модуля
 */
export const createModuleItemSchema = z.object({
  type: moduleItemTypeEnum,
  isPreview: z.boolean().default(false),
  videoUrl: z.string().url().nullable().optional(),
  videoDuration: z.number().int().positive().nullable().optional(),
  /** Ссылка на книгу в библиотеке — для type === 'BOOK'. */
  bookId: z.number().int().positive().nullable().optional(),
  /** Кастомный постер урока (только для видео-уроков). Переопределяет авто-кадр из video-processor. */
  posterUrl: z.string().url().nullable().optional(),
  posterFileId: z.number().int().positive().nullable().optional(),
  availableFrom: z.coerce.date().nullable().optional(),
  translations: z.array(moduleItemTranslationSchema).min(1, 'Требуется хотя бы один перевод'),
  homeworkConfig: z.object({
    homeworkType: z.enum(['ONE_TIME', 'DAILY_STREAK', 'PERIOD']),
    deadline: z.string().nullable().optional(),
    streakDays: z.number().int().positive().nullable().optional(),
    periodHours: z.number().int().positive().nullable().optional(),
  }).optional(),
});

/**
 * Схема обновления элемента модуля
 */
export const updateModuleItemSchema = createModuleItemSchema.partial();

/**
 * Схема для изменения порядка элементов
 */
export const reorderModuleItemsSchema = z.object({
  itemIds: z.array(z.number().int()).min(1, 'Требуется хотя бы один элемент'),
});

// ============================================
// Progress Schemas
// ============================================

/**
 * Схема обновления прогресса
 */
export const updateProgressSchema = z.object({
  completedItemId: z.number().int().optional(),
  lastWatchedItemId: z.number().int().optional(),
  lastPosition: z.number().int().min(0).optional(),
  /** Процент просмотра элемента (0-100). При >= 90% элемент автоматически завершается */
  watchProgress: z.number().int().min(0).max(100).optional(),
  /**
   * Реальная длительность видео в секундах, полученная плеером из metadata.
   * Источник правды для ModuleItem.videoDuration и LessonProgress.duration.
   * Ограничение 86400 (24ч) — защита от мусорных значений от клиента.
   */
  videoDuration: z.number().int().min(1).max(86400).optional(),
});

// ============================================
// Homework Schemas
// ============================================

/**
 * Схема фильтров для списка ДЗ
 */
export const homeworkFiltersSchema = z.object({
  status: z.enum(['NOT_STARTED', 'SUBMITTED', 'REVIEWED']).optional(),
  userId: z.coerce.number().int().positive().optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

/**
 * Схема рецензии на ДЗ
 */
export const reviewHomeworkSchema = z.object({
  grade: z.number().int().min(0).max(100).optional(),
  feedback: z.string().max(5000).optional(),
  status: z.enum(['REVIEWED']),
});

// ============================================
// Type Exports
// ============================================

export type CreateCourseInput = z.infer<typeof createCourseSchema>;
export type UpdateCourseInput = z.infer<typeof updateCourseSchema>;
export type CourseFilters = z.infer<typeof courseFiltersSchema>;
export type ReorderCoursesInput = z.infer<typeof reorderCoursesSchema>;

export type CreateModuleInput = z.infer<typeof createModuleSchema>;
export type UpdateModuleInput = z.infer<typeof updateModuleSchema>;
export type ReorderModulesInput = z.infer<typeof reorderModulesSchema>;

export type CreateModuleItemInput = z.infer<typeof createModuleItemSchema>;
export type UpdateModuleItemInput = z.infer<typeof updateModuleItemSchema>;
export type ReorderModuleItemsInput = z.infer<typeof reorderModuleItemsSchema>;

export type UpdateProgressInput = z.infer<typeof updateProgressSchema>;
export type HomeworkFilters = z.infer<typeof homeworkFiltersSchema>;
export type ReviewHomeworkInput = z.infer<typeof reviewHomeworkSchema>;

// ============================================
// Response/Domain Types
// ============================================

/**
 * Курс с минимальными связями (без translations)
 */
export interface CourseWithDetails {
  id: number;
  slug: string;
  durationMinutes: number | null;
  lessonsCount: number;
  thumbnailUrl: string | null;
  previewVideoUrl: string | null;
  order: number;
  isPublished: boolean;
  instructorId: number | null;
  createdAt: Date;
  updatedAt: Date;
  modules?: ModuleWithDetails[];
  instructor?: TeacherData | null;
}

export interface ModuleWithDetails {
  id: number;
  courseId: number;
  order: number;
  createdAt: Date;
  updatedAt: Date;
  items?: ModuleItemWithDetails[];
}

export interface ModuleItemWithDetails {
  id: number;
  moduleId: number;
  type: string;
  order: number;
  isPreview: boolean;
  videoUrl: string | null;
  videoDuration: number | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface TeacherData {
  id: number;
  photoUrl: string | null;
}

/**
 * Локализованный курс (для публичного API)
 * Переводы добавляются в контроллере через TranslationService
 */
export interface LocalizedCourse {
  id: number;
  slug: string;
  title: string;
  description: string | null;
  shortDescription: string | null;
  location: string | null;
  venue: string | null;
  durationMinutes: number | null;
  lessonsCount: number;
  thumbnailUrl: string | null;
  bannerImageUrl: string | null;
  pastImageUrl: string | null;
  previewVideoUrl: string | null;
  isPublished: boolean;
  instructor: { id: number; name: string; role: string | null; photoUrl: string | null } | null;
  instructors: { id: number; name: string; role: string | null; photoUrl: string | null }[];
  // Тип курса и даты (courseType теперь text, возвращает string)
  courseType: string;
  startDate: string | null;
  endDate: string | null;
  // Часы проведения в формате HH:MM (например '18:00'–'20:00'), опциональны
  courseHoursFrom: string | null;
  courseHoursTo: string | null;
  schedule: { date: string; time: string; moduleId?: number }[] | null;
  // Длительность доступа
  accessDurationDays: number | null;
  // Ценовая информация
  price?: number | null;
  currency?: string;
  // Ценообразование через PricingTier
  isPurchasable: boolean;
  pricingTiers: LocalizedPricingTier[];
  // Агрегат для карточки каталога: «от {minPrice}» (минимум среди тарифов,
  // promo учтена). null — если у курса нет платных тарифов.
  minPrice: number | null;
  minPriceBaseCurrency: string | null;
  // Meeting/offline specific
  capacity: number | null;
  bookedSeats: number | null;
}

/** JSON-compatible value for jsonb columns */
export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

export interface ConvertedPrice {
  price: number;
  isManual: boolean;
}

export interface LocalizedPricingTier {
  id: number;
  durationDays: number | null;
  prices: Record<string, number>;
  baseCurrency: string;
  allPrices: Record<string, ConvertedPrice>;
  originalPrices: Record<string, number> | null;
  allOriginalPrices: Record<string, ConvertedPrice> | null;
  discountEndsAt: Date | null;
  order: number;
  name: string | null;
  shortDescription: string | null;
  bonuses: LocalizedBonus[];
}

export interface LocalizedBonus {
  id: number;
  type: string;
  initialValue: JsonValue;
  durationMode: string;
  durationDays: number | null;
  expiresAt: Date | null;
  name: string | null;
  description: string | null;
}

export interface CourseLocalizationSource {
  id: number;
  slug: string;
  durationMinutes: number | null;
  lessonsCount: number;
  thumbnailUrl: string | null;
  bannerImageId: number | null;
  pastImageId: number | null;
  previewVideoUrl: string | null;
  isPublished: boolean;
  instructor: { id: number; photoUrl: string | null } | null;
  instructors?: { id: number; photoUrl: string | null }[];
  // Тип курса и даты (courseType теперь text, возвращает string)
  courseType: string;
  startDate: Date | null;
  endDate: Date | null;
  // Часы проведения в формате HH:MM (например '18:00'–'20:00'), опциональны
  courseHoursFrom: string | null;
  courseHoursTo: string | null;
  schedule: unknown; // jsonb
  // Длительность доступа
  accessDurationDays: number | null;
  // Ценовая информация
  price: string | null; // numeric приходит как string
  currency: string | null;
  isPurchasable: boolean;
  // Meeting/offline specific
  capacity: number | null;
  bookedSeats: number | null;
}

/**
 * Локализованный модуль
 */
export interface LocalizedModule {
  id: number;
  order: number;
  title: string;
  description: string | null;
  /** Абсолютная дата открытия модуля. null = доступен сразу. */
  availableFrom: string | null;
  /** true если дата открытия в будущем (computed). */
  isLocked: boolean;
  items: LocalizedModuleItem[];
}

/**
 * Локализованный элемент модуля
 */
export interface LinkedLivestreamInfo {
  id: number;
  status: string;
  scheduledAt: string | null;
}

export interface LocalizedModuleItem {
  id: number;
  order: number;
  type: string;
  title: string;
  isPreview: boolean;
  videoDuration: number | null;
  linkedLivestream: LinkedLivestreamInfo | null;
  /** Эффективная дата открытия (урок ?? модуль). null = доступен сразу. */
  availableFrom: string | null;
  /** true если дата открытия в будущем (computed). */
  isLocked: boolean;
}

/**
 * Результат пагинации
 */
export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Прогресс пользователя с деталями
 */
export interface UserCourseProgress {
  courseId: number;
  course: LocalizedCourse;
  completedItems: number[];
  totalItems: number;
  progressPercent: number;
  lastWatchedItem: number | null;
  lastPosition: number | null;
  completedAt: Date | null;
  startedAt: Date;
}

export interface UserCourseProgressItem {
  id: number;
  userId: number;
  courseId: number;
  completedItems: number[];
  lastWatchedItem: number | null;
  lastPosition: number | null;
  completedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
  course: LocalizedCourse;
  /** Процент прогресса (0-100) */
  progress?: number;
}

/**
 * ДЗ с деталями
 */
export interface ChecklistItem {
  text: string;
  isCompleted: boolean;
}

export interface NoteItem {
  id: string;
  text: string;
  createdAt: string;
}

export interface HomeworkWithDetails {
  id: number;
  userId: number;
  itemId: number;
  status: string;
  files: { url: string; name: string; size: number }[] | null;
  checklistItems: ChecklistItem[] | null;
  notes: string | null;
  noteItems: NoteItem[] | null;
  submittedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
  item: {
    id: number;
    type: string;
    title: string;
    description: string | null;
    module: {
      id: number;
      title: string;
      course: {
        id: number;
        slug: string;
        title: string;
      };
    };
  };
  user?: {
    id: number;
    firstName: string | null;
    lastName: string | null;
    telegramUsername: string | null;
  };
}
